import React, { FunctionComponent } from "react";

import { initializeFeatureFlagProvider } from "@worktango/feature-flags-react";
import { createRoot } from "react-dom/client";

import type { IApplicationComponentProps } from "@kazoohr/types";

export async function browserEntry(
  component: FunctionComponent<IApplicationComponentProps>
): Promise<void> {
  initializeFeatureFlagProvider({
    sdkKey: window.kazoo.devCycleClientSdkKey,
    shouldEnableDebugger: window.kazoo.devCycleShouldEnableWebDebugger,
  });

  /**
   * Why are we loading these like this? Because we don't want the below to block
   * the initial render! These will be loaded asynchronously.
   */
  await Promise.all([
    import("@kazoohr/graphql"),
    import("@kazoohr/internationalization"),
    import("@kazoohr/layout-components"),
    import("@kazoohr/router"),
  ] as const).then(
    async ([
      { setupClient },
      { initializeClientI18n },
      { configureLayout },
      { Router },
    ]) => {
      const { initialProps, i18nProps, layout } = window.kazoo;

      const i18n = await initializeClientI18n(i18nProps);
      const { apiClient, client } = setupClient(initialProps);

      const props = {
        apiClient,
        client,
        component,
        initialProps,
        i18n,
      };

      const Layout = configureLayout(layout);

      // We need to do hot module _imports_ here, not exports. That's why we need
      // the AppContainer component here. If we did exports, every app would need to
      // use `export default hot(MyApp);`.
      const container = document.getElementById("root");
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const root = createRoot(container!);
      root.render(
        <Router>
          <Layout {...props} />
        </Router>
      );

      // Kick off Pendo last so we don't block interactivity
      window.kazoo.initializePendo?.();
    }
  );
  return;
}
